// src/components/ui/ThemeToggle.tsx

import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "@/hooks/useTheme";

export default function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();

  const isDark = theme === "dark";

  return (
    <motion.button
      type="button"
      onClick={toggleTheme}
      title={`Change theme to ${isDark ? "light" : "dark"}`}
      aria-label="Toggle theme"
      whileTap={{ scale: 0.96 }}
      className="border-border bg-surface text-text hover:bg-accent-bg relative flex h-9 w-9 cursor-pointer items-center justify-center overflow-hidden rounded-full border shadow-sm transition-colors duration-300"
    >
      {/* Ambient glow */}
      <motion.div
        animate={{
          scale: isDark ? 1.2 : 1,
          opacity: isDark ? 0.18 : 0.1,
        }}
        transition={{ duration: 0.4 }}
        className="bg-accent absolute inset-0 rounded-full blur-xl"
      />

      {/* Rotating background ring */}
      <motion.div
        animate={{
          rotate: isDark ? 180 : 0,
        }}
        transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="border-accent/20 absolute inset-0.75 rounded-full border"
      />

      {/* Icon swap */}
      <AnimatePresence mode="wait" initial={false}>
        {isDark ? (
          <motion.svg
            key="sun"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ rotate: -90, scale: 0.4, opacity: 0 }}
            animate={{ rotate: 0, scale: 1, opacity: 1 }}
            exit={{ rotate: 90, scale: 0.4, opacity: 0 }}
            transition={{
              duration: 0.35,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="text-accent relative z-10 h-5 w-5"
          >
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2" />
            <path d="M12 20v2" />
            <path d="m4.93 4.93 1.41 1.41" />
            <path d="m17.66 17.66 1.41 1.41" />
            <path d="M2 12h2" />
            <path d="M20 12h2" />
            <path d="m6.34 17.66-1.41 1.41" />
            <path d="m19.07 4.93-1.41 1.41" />
          </motion.svg>
        ) : (
          <motion.svg
            key="moon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            initial={{ rotate: 90, scale: 0.4, opacity: 0 }}
            animate={{ rotate: 0, scale: 1, opacity: 1 }}
            exit={{ rotate: -90, scale: 0.4, opacity: 0 }}
            transition={{
              duration: 0.35,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="text-accent relative z-10 h-5 w-5"
          >
            <path d="M21 12.79A9 9 0 1 1 11.21 3c0 .34.02.67.05 1A7 7 0 0 0 20 12.74c.33.03.66.05 1 .05Z" />
          </motion.svg>
        )}
      </AnimatePresence>

      {/* Subtle pulse */}
      <motion.div
        animate={{
          scale: isDark ? [1, 1.12, 1] : [1, 1.06, 1],
          opacity: isDark ? [0.08, 0.16, 0.08] : [0.05, 0.1, 0.05],
        }}
        transition={{
          repeat: Infinity,
          duration: 2.8,
          ease: "easeInOut",
        }}
        className="border-accent/20 absolute inset-0 rounded-full border"
      />
    </motion.button>
  );
}
